import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [detail, setDetail] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      if (cancelled) return;
      tries++;
      try {
        const { data } = await api.get(`/api/payments/status/${sessionId}`);
        setDetail(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
      } catch (e) {
        // continue polling
      }
      if (tries < 20) setTimeout(poll, 1500);
      else setStatus("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center">
      {status === "polling" && (
        <>
          <Loader2 className="w-10 h-10 animate-spin mx-auto text-muted-foreground mb-6" />
          <div className="eyebrow text-muted-foreground mb-2">Please wait</div>
          <h1 className="font-heading text-3xl font-light">Confirming your payment…</h1>
        </>
      )}
      {status === "paid" && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="w-16 h-16 border-2 border-foreground mx-auto mb-8 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <div className="eyebrow text-muted-foreground mb-2">Success</div>
          <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tighter mb-4" data-testid="payment-success">Payment received.</h1>
          <p className="text-muted-foreground mb-8">Your plan and credits are already active.</p>
          <div className="flex justify-center gap-3">
            <Link to="/dashboard"><Button className="rounded-none bg-foreground text-background hover:bg-foreground/90">Go to dashboard</Button></Link>
            <Link to="/billing"><Button variant="outline" className="rounded-none border-foreground">View billing</Button></Link>
          </div>
        </motion.div>
      )}
      {(status === "failed" || status === "error" || status === "timeout") && (
        <>
          <div className="eyebrow text-destructive mb-2">Hmm</div>
          <h1 className="font-heading text-3xl font-light mb-4">Payment status unclear</h1>
          <p className="text-muted-foreground mb-6">If the amount was charged, please check your billing page in a moment or contact support.</p>
          <Link to="/billing"><Button className="rounded-none">Go to billing</Button></Link>
        </>
      )}
    </div>
  );
}
